/**
 * Session Results Page
 * 
 * Displays the persisted session report (read-model) per Doc 07 and Decision D-002.
 * 
 * This page renders the immutable session report created when the session completed.
 * It reads from the session_reports table, NOT from live session data.
 * 
 * Content varies by intent:
 * - select_attributes: Chosen criteria + provenance
 * - converge_values: Accepted values + unknowns
 * - select_item_pool: Membership + inclusion reasons
 * - rank_priorities: Ranks/scores + overrides
 */

import { SessionItemChangesNote } from '../../../components/prioritization/session/SessionItemChangesNote';
import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { UI_LABELS } from '../../../lib/terminology';
import {
  ArrowLeft,
  FileText,
  Clock,
  User,
  Target,
  BarChart3,
  CheckCircle,
  AlertCircle,
  ListChecks,
  Trophy,
  RefreshCw,
  MessageSquare,
} from 'lucide-react';
import {
  coerceRankingScore,
  useSessionReportStore,
  type SessionReport,
  type SessionReportIntent,
} from '../../../stores/prioritization/sessionReportStore';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { sessionHasPrioritizationResults, type PrioritizationSession } from '../../../types/prioritization/session';
import { SessionMemoAction } from '../../../components/prioritization/memo/SessionMemoAction';
import { ItemRelationshipFlags } from '@/components/prioritization/ItemRelationshipFlags';
import { RankMark } from '@/components/prioritization/RankMark';
import { StructureVersionFinePrint } from '@/components/hierarchy/StructureVersionFinePrint';

function buildLiveRankingReport(session: PrioritizationSession): SessionReport {
  const rankedItemIds = session.rankingOutput!.rankedItemIds;
  const scores = session.rankingOutput!.scores ?? {};
  return {
    id: `live-${session.id}`,
    sessionId: session.id,
    cycleId: session.cycleId,
    trackId: session.trackId,
    focusListId: session.resultingFocusListId,
    intent: 'rank_priorities',
    inputsSnapshot: {
      itemIds: session.itemIds ?? [],
      criteriaIds: session.criteriaConfig?.criteriaIds ?? [],
      criteriaWeights: session.criteriaConfig?.weights ?? {},
      participants: session.participantIds ?? [],
    },
    outputsSnapshot: {
      ranking: rankedItemIds.map((itemId, index) => ({
        itemId,
        rank: index + 1,
        score: coerceRankingScore(scores[itemId]),
      })),
      cutLinePosition: session.cutLinePosition,
      toolUsed: session.selectedToolId ?? session.rankingOutput?.toolId,
    },
    deltasSnapshot: {},
    // Live (not-yet-persisted) results have no session_reports row, so there
    // is no server-filled Deliberation Record snapshot yet — the empty shape,
    // not a guess.
    deliberationSnapshot: { transcriptStatus: 'none', messageCount: 0, annotations: [], overrides: [], artifacts: [] },
    createdBy: session.facilitatorId ?? 'system',
    createdAt: session.runCompletedAt ?? session.updatedAt ?? new Date(),
    version: 1,
    isFinal: false,
  };
}

// ============================================================================
// Intent Display Components
// ============================================================================

function AttributeSelectionResult({ report }: { report: SessionReport }) {
  const criteriaIds = report.inputsSnapshot?.criteriaIds || [];
  const weights = report.inputsSnapshot?.criteriaWeights || {};
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-[var(--text-pri)]">Selected Criteria</h3>
      
      {criteriaIds.length === 0 ? (
        <p className="text-[var(--text-ter)]">No criteria were selected in this session.</p>
      ) : (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] divide-y divide-[var(--rule)]">
          {criteriaIds.map((criterionId) => (
            <div key={criterionId} className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <Target className="w-4 h-4 text-primary-600" />
                </div>
                <span className="font-medium text-[var(--text-pri)]">{criterionId}</span>
              </div>
              <div className="text-sm text-[var(--text-ter)]">
                Weight: {weights[criterionId] || 1}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ValueConvergenceResult({ report }: { report: SessionReport }) {
  const acceptedValues = report.outputsSnapshot?.acceptedValues || [];
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-[var(--text-pri)]">Accepted Values</h3>
      
      {acceptedValues.length === 0 ? (
        <p className="text-[var(--text-ter)]">No values were converged in this session.</p>
      ) : (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] divide-y divide-[var(--rule)]">
          {acceptedValues.map((av, index) => (
            <div key={index} className="px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-[var(--text-ter)] mb-1">
                <span>Item: {av.itemId}</span>
                <span>•</span>
                <span>Attribute: {av.attributeKey}</span>
              </div>
              <div className="font-medium text-[var(--text-pri)]">
                Value: {String(av.value)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CandidatePoolResult({ report }: { report: SessionReport }) {
  const itemIds = useMemo(() => report.inputsSnapshot?.itemIds || [], [report.inputsSnapshot]);
  const items = useItemsStore(state => state.items);
  
  const selectedItems = useMemo(() => {
    return itemIds.map(id => {
      const item = items.find(c => c.id === id);
      return {
        id,
        title: item?.title || id,
      };
    });
  }, [itemIds, items]);
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-[var(--text-pri)]">{UI_LABELS.itemPool}</h3>

      <div className="bg-[var(--surface)] p-4 mb-4">
        <div className="text-2xl font-bold text-[var(--text-pri)]">{itemIds.length}</div>
        <div className="text-sm text-[var(--text-ter)]">Items in pool</div>
      </div>
      
      {selectedItems.length > 0 && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] divide-y divide-[var(--rule)] max-h-96 overflow-auto">
          {selectedItems.map((item, index) => (
            <div key={item.id} className="px-4 py-3 flex items-center gap-3">
              <div className="w-6 h-6 bg-[var(--hover-tint)] rounded-full flex items-center justify-center text-xs text-[var(--text-ter)]">
                {index + 1}
              </div>
              <span className="text-[var(--text-pri)]">{item.title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Ranking result, with the prioritization memo beneath it
// ============================================================================

function RankingResult({ report, sessionId }: { report: SessionReport; sessionId?: string }) {
  const ranking = useMemo(() => report.outputsSnapshot?.ranking || [], [report.outputsSnapshot]);
  const cutLine = report.outputsSnapshot?.cutLinePosition;
  const toolUsed = report.outputsSnapshot?.toolUsed;
  const items = useItemsStore(state => state.items);

  const rankedItems = useMemo(() => {
    return ranking.map(r => {
      const item = items.find(c => c.id === r.itemId);
      return {
        ...r,
        title: item?.title || r.itemId,
        description: item?.description,
        score: coerceRankingScore(r.score),
      };
    });
  }, [ranking, items]);
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-[var(--text-pri)]">Ranking Results</h3>
        {toolUsed && (
          <span className="text-sm text-[var(--text-ter)] bg-[var(--hover-tint)] px-2 py-1 rounded">
            Tool: {toolUsed}
          </span>
        )}
      </div>

      {/* Ranking ≠ Committed Priority disclaimer */}
      <div className="flex items-start gap-3 border border-[var(--mustard)] bg-[var(--mustard-dim)] px-4 py-3">
        <AlertCircle className="h-4 w-4 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
        <div className="text-sm text-[var(--mustard)]">
          <span className="font-medium">Ranking informs priority; it does not establish it.</span>
          {' '}To commit these results as organizational priorities, submit them through{' '}
          <Link to="/prioritization/catchball" className="underline font-medium hover:text-[var(--mustard)]">
            Catchball
          </Link>.
          {' '}A ranking output becomes an Accepted Priority only after it passes through Proposal Formation and is accepted by the governing tier.
        </div>
      </div>

      {ranking.length === 0 ? (
        <p className="text-[var(--text-ter)]">No ranking output recorded.</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-[var(--surface)] p-4">
              <div className="text-2xl font-bold text-[var(--text-pri)]">{ranking.length}</div>
              <div className="text-sm text-[var(--text-ter)]">Items ranked</div>
            </div>
            {cutLine !== undefined && (
              <div className="bg-[var(--green-dim)] p-4">
                <div className="text-2xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{cutLine}</div>
                <div className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">Cut line position</div>
              </div>
            )}
            {cutLine !== undefined && (
              <div className="bg-[var(--mustard-dim)] p-4">
                <div className="text-2xl font-bold text-[var(--mustard)]">{ranking.length - cutLine}</div>
                <div className="text-sm text-[var(--mustard)]">Below the line</div>
              </div>
            )}
          </div>
          
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] divide-y divide-[var(--rule)]">
            {rankedItems.map((item) => {
              const isAboveLine = cutLine === undefined || item.rank <= cutLine;

              return (
                <div
                  key={item.itemId}
                  className={`px-4 py-3 flex items-center justify-between ${
                    !isAboveLine ? 'bg-[var(--surface)] text-[var(--text-ter)]' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <RankMark
                      rank={item.rank}
                      official={false}
                      tied={rankedItems.filter((other) => other.rank === item.rank).length > 1}
                      aboveTheLine={isAboveLine}
                    />
                    <span className={isAboveLine ? 'text-[var(--text-pri)]' : 'text-[var(--text-ter)]'}>
                      {item.title}
                    </span>
                    <ItemRelationshipFlags itemId={item.itemId} />
                  </div>
                  {typeof item.score === 'number' && (
                    <div className="text-base font-mono text-[var(--text-ter)]">
                      Score: {item.score.toFixed(2)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {sessionId && (
            <div className="mt-6">
              <SessionMemoAction sessionId={sessionId} variant="panel" />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================================
// Intent Icon/Label helpers
// ============================================================================

function getIntentIcon(intent: SessionReportIntent) {
  switch (intent) {
    case 'select_attributes':
      return Target;
    case 'converge_values':
      return CheckCircle;
    case 'select_item_pool':
      return ListChecks;
    case 'rank_priorities':
      return Trophy;
    default:
      return BarChart3;
  }
}

function getIntentLabel(intent: SessionReportIntent): string {
  switch (intent) {
    case 'select_attributes':
      return 'Attribute Selection';
    case 'converge_values':
      return 'Value Convergence';
    case 'select_item_pool':
      return `${UI_LABELS.itemPool} Selection`;
    case 'rank_priorities':
      return 'Priority Ranking';
    case 'calibration':
      return 'Calibration';
    default:
      return 'Session Results';
  }
}

// ============================================================================
// Deliberation snapshot — "Why" (Doc 08 §4; Doc 21 §6.4)
//
// migration 726 fills session_reports.deliberation_snapshot server-side at
// report creation, but nothing read it back until now. Annotations/overrides
// are the rationale recorded AT THE TIME — the report is immutable, so later
// changes to the live deliberation record never appear here.
// ============================================================================

function DeliberationSection({ report }: { report: SessionReport }) {
  const snap = report.deliberationSnapshot;
  const items = useItemsStore(state => state.items);
  const itemTitle = (id: string) => items.find(i => i.id === id)?.title ?? id;

  const hasContent = snap.annotations.length > 0 || snap.overrides.length > 0 || snap.artifacts.length > 0;
  if (!hasContent && snap.transcriptStatus === 'none') return null;

  return (
    <div className="mt-8 pt-6 border-t border-[var(--rule)]">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="h-4 w-4 text-[var(--text-ter)]" />
        <h3 className="text-sm font-medium text-[var(--text-ter)]">Why — rationale recorded at the time</h3>
      </div>

      {!hasContent ? (
        <p className="text-sm text-[var(--text-ter)]">
          No annotations, overrides, or artifacts were recorded during this session.
        </p>
      ) : (
        <div className="space-y-4">
          {snap.overrides.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[var(--text-ter)] mb-1.5">Overrides</p>
              <ul className="space-y-2">
                {snap.overrides.map((o, i) => (
                  <li key={i} className="bg-[var(--surface)] px-3 py-2 text-sm">
                    <span className="font-medium text-[var(--text-pri)]">{itemTitle(o.itemId)}</span>
                    {o.reason && <p className="text-[var(--text-sec)] mt-0.5">{o.reason}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {snap.annotations.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[var(--text-ter)] mb-1.5">Annotations</p>
              <ul className="space-y-2">
                {snap.annotations.map((a, i) => (
                  <li key={i} className="bg-[var(--surface)] px-3 py-2 text-sm">
                    <span className="font-medium text-[var(--text-pri)]">{itemTitle(a.itemId)}</span>
                    <p className="text-[var(--text-sec)] mt-0.5">{a.content}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {snap.artifacts.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[var(--text-ter)] mb-1.5">Artifacts</p>
              <ul className="space-y-1">
                {snap.artifacts.map((art, i) => (
                  <li key={i} className="text-sm">
                    {art.url ? (
                      <a href={art.url} target="_blank" rel="noreferrer" className="text-primary-600 hover:text-primary-800 hover:underline">
                        {art.title || art.type}
                      </a>
                    ) : (
                      <span className="text-[var(--text-sec)]">{art.title || art.type}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {snap.messageCount > 0 && (
        <p className="text-xs text-[var(--text-ter)] mt-3">
          {snap.messageCount} deliberation message{snap.messageCount === 1 ? '' : 's'} recorded.
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function SessionResults() {
  const { id: sessionId } = useParams<{ id: string }>();
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const report = useSessionReportStore(state => state.getReportForSession(sessionId || ''));
  const fetchReports = useSessionReportStore(state => state.fetchReportsForSession);
  
  const session = useSessionStore(state => state.getSession(sessionId || ''));
  const fetchSessions = useSessionStore(state => state.fetchFromSupabase);
  const fetchSessionById = useSessionStore(state => state.fetchSessionById);
  
  const liveRankingReport = useMemo(
    () => (session && sessionHasPrioritizationResults(session) ? buildLiveRankingReport(session) : null),
    [session]
  );
  const displayReport = report ?? liveRankingReport;
  
  // Fetch data on mount
  useEffect(() => {
    async function loadData() {
      if (!sessionId) {
        setError('No session ID provided');
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch both session and report
        await Promise.all([
          fetchSessions(),
          fetchReports(sessionId),
          fetchSessionById(sessionId),
        ]);
      } catch (err) {
        console.error('[SessionResults] Failed to load data:', err);
        setError('Failed to load session results');
      } finally {
        setIsLoading(false);
      }
    }
    
    loadData();
  }, [sessionId, fetchSessions, fetchReports, fetchSessionById]);
  
  // Loading state
  if (isLoading) {
    return (
      <div className="p-6 max-w-screen-2xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 text-[var(--text-ter)] animate-spin" />
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="p-6 max-w-screen-2xl mx-auto">
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-6 text-center">
          <AlertCircle className="h-8 w-8 text-[var(--coral)] mx-auto mb-2" />
          <p className="text-[var(--coral)]">{error}</p>
        </div>
      </div>
    );
  }
  
  // No report found — fall back to live ranking_output when the session has results
  if (!displayReport) {
    return (
      <div className="p-6 max-w-screen-2xl mx-auto">
        <div className="mb-6">
          <Link 
            to={session ? `/prioritization/sessions/${sessionId}` : '/prioritization/sessions'}
            className="inline-flex items-center text-[var(--text-sec)] hover:text-[var(--text-pri)]"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to {session ? 'Session' : 'Sessions'}
          </Link>
        </div>
        
        <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-6 text-center">
          <FileText className="h-8 w-8 text-[var(--mustard)] mx-auto mb-2" />
          <h2 className="text-lg font-medium text-[var(--mustard)] mb-1">No Report Available</h2>
          <p className="text-[var(--mustard)]">
            This session has not yet generated a final report. 
            Reports are created when a session reaches RUN_COMPLETE or PUBLISHED state.
          </p>
          {session && (
            <p className="text-sm text-[var(--mustard)] mt-2">
              Current session state: <span className="font-medium">{session.state}</span>
            </p>
          )}
          {session && sessionHasPrioritizationResults(session) && (
            <Link
              to={`/prioritization/sessions/${sessionId}?tab=results`}
              className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium text-primary-700 hover:text-primary-800"
            >
              View live session results
              <ArrowLeft className="h-3.5 w-3.5 rotate-180" />
            </Link>
          )}
        </div>
      </div>
    );
  }
  
  // Render report
  const IntentIcon = getIntentIcon(displayReport.intent);
  const isLiveFallback = !report && !!liveRankingReport;
  
  return (
    <div className="p-6 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link 
          to={`/prioritization/sessions/${sessionId}`}
          className="inline-flex items-center text-[var(--text-sec)] hover:text-[var(--text-pri)]"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Session
        </Link>
      </div>
      
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
        {/* Report Header */}
        <div className="px-6 py-4 border-b border-[var(--rule)] bg-[var(--surface)]">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-primary-100 flex items-center justify-center">
              <IntentIcon className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-[var(--text-pri)]">
                {session?.name || 'Session'} Results
              </h1>
              <p className="text-sm text-[var(--text-ter)]">
                {getIntentLabel(displayReport.intent)}
                {isLiveFallback && ' · live session results'}
              </p>
              {session?.id && <StructureVersionFinePrint table="session_results" column="session_id" id={session.id} mode="made" className="mt-0.5" />}
            </div>
          </div>
          
          <div className="flex items-center gap-4 mt-4 text-sm text-[var(--text-ter)]">
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>Created: {displayReport.createdAt.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-1">
              <User className="w-4 h-4" />
              <span>By: {displayReport.createdBy}</span>
            </div>
            {displayReport.isFinal && (
              <div className="flex items-center gap-1 text-[var(--green)] dark:text-[var(--text-ter)]">
                <CheckCircle className="w-4 h-4" />
                <span>Final</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Report Content */}
        <div className="p-6">
          {session?.id && <SessionItemChangesNote sessionId={session.id} className="mb-6" />}
          {displayReport.intent === 'select_attributes' && (
            <AttributeSelectionResult report={displayReport} />
          )}
          
          {displayReport.intent === 'converge_values' && (
            <ValueConvergenceResult report={displayReport} />
          )}
          
          {displayReport.intent === 'select_item_pool' && (
            <CandidatePoolResult report={displayReport} />
          )}
          
          {displayReport.intent === 'rank_priorities' && (
            <RankingResult report={displayReport} sessionId={session?.id} />
          )}
          
          {(displayReport.intent === 'calibration' || displayReport.intent === 'other') && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-[var(--text-pri)]">Session Summary</h3>
              <div className="bg-[var(--surface)] p-4">
                <p className="text-[var(--text-sec)]">
                  This session completed with {displayReport.inputsSnapshot?.itemIds?.length || 0} items 
                  and {displayReport.inputsSnapshot?.participants?.length || 0} participants.
                </p>
              </div>
            </div>
          )}
          
          <DeliberationSection report={displayReport} />

          {/* Metadata Section */}
          <div className="mt-8 pt-6 border-t border-[var(--rule)]">
            <h3 className="text-sm font-medium text-[var(--text-ter)] mb-3">Report Metadata</h3>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-[var(--text-ter)]">Report ID</dt>
                <dd className="font-mono text-[var(--text-pri)]">{displayReport.id.slice(0, 8)}...</dd>
              </div>
              <div>
                <dt className="text-[var(--text-ter)]">Session ID</dt>
                <dd className="font-mono text-[var(--text-pri)]">{displayReport.sessionId.slice(0, 8)}...</dd>
              </div>
              {displayReport.cycleId && (
                <div>
                  <dt className="text-[var(--text-ter)]">Cycle ID</dt>
                  <dd className="font-mono text-[var(--text-pri)]">{displayReport.cycleId.slice(0, 8)}...</dd>
                </div>
              )}
              <div>
                <dt className="text-[var(--text-ter)]">Version</dt>
                <dd className="text-[var(--text-pri)]">{displayReport.version}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
